import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import store from "../redux/store";
import { envoyerPush, getHistoriquePush, getNiveaux, getStatistiquesSpotters, Niveau, toutesLesQuetes } from "../utilities/locationService";

/* ═══════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════ */
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg: "var(--c-bg)",
  card: "var(--c-surface)",
  border: "var(--c-border)",
  borderAccent: "#1F1E1B",
  accent: "#1F1E1B",
  accentLight: "#57534A",
  accentMuted: "rgba(31,30,27,0.07)",
  text: "#1F1E1B",
  textSub: "#57534A",
  textMuted: "#57534A",
  success: "#356A0F",
  warning: "#7C5600",
  error: "#A23A0D",
} as const;

/* ═══════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════ */
/* Cibles réellement atteignables. Pas de « zone » : l'app ne s'abonne à
   aucun topic de zone, un tel envoi ne toucherait personne. */
type CibleType = "tous" | "niveau" | "inactifs" | "queteProche";

/* Ligne de notification_history (GET /notification/history). */
interface HistoryEntry {
  id: number;
  titre: string;
  cible: string;
  destinataires: number;
  sent_at: string;
  statut: string;
}

/* ═══════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════ */
function fmtDate(iso: string): string {
  const d = new Date(String(iso).replace(" ", "T"));
  return isNaN(d.getTime()) ? String(iso) : d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* La cible est enregistrée par le serveur avec son paramètre (niveau:2, users:12…). */
function cibleLabel(cible: string): string {
  const [type, valeur] = String(cible ?? "").split(":");
  switch (type) {
    case "tous": return "Tous les Spotters";
    case "niveau": return `Niveau ${valeur}`;
    case "inactifsJours": return `Inactifs depuis ${valeur} j`;
    case "users": return `Liste de ${valeur} Spotter(s)`;
    case "user": return "Un Spotter";
    case "zone": return `Zone ${valeur ?? ""}`;
    case "queteProche": return "Proches d'une quête";
    default: return cible || "—";
  }
}

/* ═══════════════════════════════════════════════
   PHONE PREVIEW
═══════════════════════════════════════════════ */
function PhonePreview({ titre, corps }: { titre: string; corps: string }) {
  return (
    <Box
      sx={{
        background: C.card,
        border: `1px solid ${C.borderAccent}`,
        borderRadius: "8px",
        p: 2.5,
        minWidth: 260,
        maxWidth: 320,
      }}
    >
      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: C.textMuted,
          mb: 1.5,
        }}
      >
        Aperçu notification
      </Typography>

      {/* Notification bubble */}
      <Box
        sx={{
          background: "rgba(31,30,27,0.06)",
          border: `1px solid ${C.border}`,
          borderRadius: "8px",
          p: 1.5,
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
        }}
      >
        {/* App icon placeholder */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            background: C.accent,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: "16px", lineHeight: 1 }}>T</Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.3 }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: C.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "140px",
              }}
            >
              {titre || "Titre du message"}
            </Typography>
            <Typography sx={{ fontSize: "10px", color: C.textMuted, ml: 1, flexShrink: 0 }}>
              Maintenant
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "11px",
              color: C.textSub,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.4,
            }}
          >
            {corps || "Corps du message affiché ici…"}
          </Typography>
        </Box>
      </Box>

      <Typography sx={{ fontSize: "10px", color: C.textMuted, mt: 1.5, textAlign: "center" }}>
        TRIBE · Application Spotters
      </Typography>
    </Box>
  );
}

/* ═══════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════ */
export default function Notifications({ noHeader = false }: { noHeader?: boolean }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoggedIn = useSelector((state: any) => state.auth?.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth?.isTokenExpired);

  /* ── Form state ── */
  const [titre, setTitre] = useState("");
  const [corps, setCorps] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [cible, setCible] = useState<CibleType>("tous");
  const [niveaux, setNiveaux] = useState<Niveau[]>([]);
  const [niveau, setNiveau] = useState<number>(1);
  const [inactiveDays, setInactiveDays] = useState<string>("30");
  const [quetesPubliees, setQuetesPubliees] = useState<{ id: string; titre: string }[]>([]);
  const [questId, setQuestId] = useState<string>("");
  const [sending, setSending] = useState(false);

  /* ── Inactive fetch state ── */
  const [fetchingInactive, setFetchingInactive] = useState(false);
  const [inactiveUserIds, setInactiveUserIds] = useState<string[]>([]);
  const [inactiveFetched, setInactiveFetched] = useState(false);

  /* ── History (serveur) ── */
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyError, setHistoryError] = useState(false);


  const chargerHistorique = async () => {
    try {
      setHistory(await getHistoriquePush());
      setHistoryError(false);
    } catch {
      setHistoryError(true);
    }
  };

  /* Niveaux du référentiel (D7) : seuls les niveaux actifs peuvent être attribués. */
  useEffect(() => {
    chargerHistorique();
    getNiveaux()
      .then((liste) => {
        const actifs = liste.filter((n) => n.actif);
        setNiveaux(actifs);
        if (actifs.length > 0) setNiveau(actifs[0].niveau);
      })
      .catch(() => setNiveaux([]));
    // Seules les quêtes publiées : les autres ne sont visibles d'aucun Spotter.
    toutesLesQuetes()
      .then((liste) => setQuetesPubliees(liste
        .filter((q: any) => q.statut === "publiee" && q.id)
        .map((q: any) => ({ id: String(q.id), titre: String(q.titre ?? q.id) }))))
      .catch(() => setQuetesPubliees([]));
  }, []);

  /* ── Reset inactive state when cible changes ── */
  useEffect(() => {
    setInactiveUserIds([]);
    setInactiveFetched(false);
  }, [cible, inactiveDays]);

  /* ── Spotters inactifs : dernier POI (agrégat serveur) antérieur au seuil ── */
  const fetchInactiveCollectors = async () => {
    const days = parseInt(inactiveDays, 10);
    if (isNaN(days) || days <= 0) {
      dispatch(openSnackbar("Veuillez entrer un nombre de jours valide.", "red"));
      return;
    }
    setFetchingInactive(true);
    try {
      const cutoff = Date.now() - days * 86_400_000;
      const filteredEmails = (await getStatistiquesSpotters())
        .filter((l: any) => l.email && l.dernier && new Date(l.dernier).getTime() < cutoff)
        .map((l: any) => String(l.email));

      setInactiveUserIds(filteredEmails);
      setInactiveFetched(true);
      dispatch(openSnackbar(filteredEmails.length === 0
        ? `Aucun Spotter inactif depuis ${days} jours.`
        : `${filteredEmails.length} Spotter(s) inactif(s) trouvé(s).`,
        filteredEmails.length === 0 ? "orange" : "green"));
    } catch {
      dispatch(openSnackbar("Erreur lors de la recherche des Spotters inactifs", "red"));
      setInactiveUserIds([]);
      setInactiveFetched(false);
    } finally {
      setFetchingInactive(false);
    }
  };

  /* ── Build payload (champs lus par le serveur : niveauCible, userIds) ── */
  const buildPayload = (): any | null => {
    if (!titre.trim()) {
      dispatch(openSnackbar("Le titre est requis.", "red"));
      return null;
    }
    if (!corps.trim()) {
      dispatch(openSnackbar("Le corps du message est requis.", "red"));
      return null;
    }
    const lien = deepLink.trim();
    if (lien && !lien.startsWith("tribe://")) {
      dispatch(openSnackbar("Le lien doit commencer par tribe://", "red"));
      return null;
    }
    const base = { titre: titre.trim(), corps: corps.trim(), deepLink: lien };

    switch (cible) {
      case "tous":
        return { ...base, cible: "tous" };
      case "niveau":
        if (niveaux.length === 0) {
          dispatch(openSnackbar("Référentiel des niveaux indisponible.", "red"));
          return null;
        }
        return { ...base, cible: "niveau", niveauCible: niveau };
      case "inactifs":
        if (!inactiveFetched) {
          dispatch(openSnackbar("Veuillez d'abord rechercher les Spotters inactifs.", "red"));
          return null;
        }
        if (inactiveUserIds.length === 0) {
          dispatch(openSnackbar("Aucun destinataire dans la liste.", "red"));
          return null;
        }
        // La liste affichée est exactement celle qui reçoit le push.
        return { ...base, cible: "users", userIds: inactiveUserIds };
      case "queteProche":
        if (!questId) {
          dispatch(openSnackbar("Choisissez une quête.", "red"));
          return null;
        }
        return { ...base, cible: "queteProche", questId };
      default:
        return null;
    }
  };

  const cibleLisible = (): string => {
    switch (cible) {
      case "tous": return "TOUS les Spotters";
      case "niveau": return `les Spotters de niveau ${niveau}`;
      case "inactifs": return `${inactiveUserIds.length} Spotter(s) inactif(s) depuis ${inactiveDays} jours`;
      case "queteProche": {
        const q = quetesPubliees.find((x) => x.id === questId);
        return `les Spotters éligibles proches de la quête « ${q?.titre ?? questId} »`;
      }
    }
  };

  /* ── Envoi immédiat (aucune planification : rien ne l'exécuterait côté serveur) ── */
  const handleSend = async () => {
    const payload = buildPayload();
    if (!payload) return;
    // Un push part immédiatement vers des Spotters réels : confirmation.
    if (!window.confirm(`Envoyer maintenant ce push à ${cibleLisible()} ?\n\n« ${payload.titre} »`)) return;

    setSending(true);
    dispatch(loaderOn());
    try {
      const res = await envoyerPush(payload);
      const n = res?.destinataires;
      dispatch(openSnackbar(
        typeof n === "number"
          ? (n > 0 ? `Push envoyé à ${n} appareil(s).` : "Aucun appareil joignable pour cette cible : rien n'a été envoyé.")
          : "Push envoyé aux abonnés de l'application.",
        typeof n === "number" && n === 0 ? "orange" : "green"));

      setTitre("");
      setCorps("");
      setDeepLink("");
      setCible("tous");
      setInactiveDays("30");
      setInactiveUserIds([]);
      setInactiveFetched(false);
      setQuestId("");
    } catch (err: any) {
      const msg = err?.response?.data?.erreur;
      dispatch(openSnackbar(msg ? `Envoi refusé : ${msg}` : "Erreur lors de l'envoi du push", "red"));
    } finally {
      setSending(false);
      dispatch(loaderOff());
      chargerHistorique();
    }
  };

  /* ══════════════════════════════════════════════
     SHARED INPUT STYLES
  ══════════════════════════════════════════════ */
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      background: "rgba(31,30,27,0.03)",
      borderRadius: "8px",
      color: C.text,
      fontSize: "13px",
      "& fieldset": { borderColor: C.border },
      "&:hover fieldset": { borderColor: "rgba(31,30,27,0.12)" },
      "&.Mui-focused fieldset": { borderColor: C.accentLight },
    },
    "& .MuiInputLabel-root": { color: C.textMuted, fontSize: "13px" },
    "& .MuiInputLabel-root.Mui-focused": { color: C.accentLight },
  };

  const selectSx = {
    background: "rgba(31,30,27,0.03)",
    borderRadius: "8px",
    color: C.text,
    fontSize: "13px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(31,30,27,0.12)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: C.accentLight },
    "& .MuiSvgIcon-root": { color: C.textMuted },
  };

  /* ════════════════════════════════════════ RENDER */
  return (
    <Box sx={{ background: noHeader ? "transparent" : C.bg, minHeight: noHeader ? undefined : "100vh", color: C.text, p: noHeader ? 0 : { xs: 2, md: 3 } }}>

      {!noHeader && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: C.text,
              lineHeight: 1.2,
            }}
          >
            Centre de notifications
          </Typography>
          <Typography sx={{ fontSize: "13px", color: C.textSub, mt: 0.5 }}>
            Envoi immédiat de push Firebase vers les Spotters
          </Typography>
        </Box>
      )}

      {/* ── Main layout: form + preview ── */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "flex-start",
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        {/* ─────────────────────── SEND FORM ─────────────────────── */}
        <Box
          sx={{
            flex: 1,
            background: C.card,
            border: "1.5px solid #1F1E1B",
            borderRadius: "8px",
            p: 3,
            minWidth: 0,
          }}
        >
          {/* Card header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: C.accentLight,
              }}
            />
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: C.text }}>
              Composer un push
            </Typography>
          </Box>

          {/* Templates rapides */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
              Templates rapides
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {[
                { label: 'Re-engagement 14j', titre: "On a besoin de toi ! 👋", corps: "Tu n'as pas collecté depuis 2 semaines. De nouvelles zones t'attendent !" },
                { label: 'Boost en cours 🔥', titre: "🔥 Boost Tips en cours !", corps: "Un boost est en cours dans l'app. Ouvre Tribe pour voir comment y participer." },
                { label: 'Challenge 🏆', titre: "🏆 Challenge lancé !", corps: "Sois le premier à collecter dans la zone et remporte la mise." },
                { label: 'Mise à jour 📱', titre: "📱 Nouvelle version disponible", corps: "Mets à jour l'app Tribe pour accéder aux nouvelles fonctionnalités." },
                { label: 'Message libre', titre: '', corps: '' },
              ].map((tpl) => (
                <Button
                  key={tpl.label}
                  size="small"
                  onClick={() => { setTitre(tpl.titre); setCorps(tpl.corps); }}
                  sx={{
                    fontSize: 11, textTransform: 'none', borderRadius: '6px',
                    border: `1px solid ${C.border}`, color: C.textSub,
                    px: 1.25, py: 0.5,
                    '&:hover': { background: C.accentMuted, borderColor: C.borderAccent, color: C.accentLight },
                  }}
                >
                  {tpl.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Titre du message */}
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Titre du message"
              value={titre}
              onChange={(e) => setTitre(e.target.value.slice(0, 50))}
              fullWidth
              size="small"
              inputProps={{ maxLength: 50 }}
              sx={inputSx}
            />
            <Typography sx={{ fontSize: "10px", color: C.textMuted, mt: 0.5, textAlign: "right" }}>
              {titre.length} / 50
            </Typography>
          </Box>

          {/* Corps du message */}
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Corps du message"
              value={corps}
              onChange={(e) => setCorps(e.target.value.slice(0, 200))}
              fullWidth
              multiline
              rows={4}
              inputProps={{ maxLength: 200 }}
              sx={inputSx}
            />
            <Typography sx={{ fontSize: "10px", color: C.textMuted, mt: 0.5, textAlign: "right" }}>
              {corps.length} / 200
            </Typography>
          </Box>

          {/* Deep link */}
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Deep link optionnel"
              value={deepLink}
              onChange={(e) => setDeepLink(e.target.value)}
              fullWidth
              size="small"
              placeholder="tribe://accueil ou tribe://quete/[id]"
              sx={inputSx}
            />
          </Box>

          {/* ── Ciblage ── */}
          <Box
            sx={{
              background: C.accentMuted,
              border: `1px solid ${C.borderAccent}`,
              borderRadius: "8px",
              p: 2,
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                color: C.accentLight,
                mb: 1.5,
              }}
            >
              Ciblage
            </Typography>

            <RadioGroup
              value={cible}
              onChange={(e) => setCible(e.target.value as CibleType)}
            >
              {/* Tous */}
              <FormControlLabel
                value="tous"
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: C.textMuted,
                      "&.Mui-checked": { color: C.accentLight },
                      p: "6px",
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "13px", color: C.text }}>
                    Tous les Spotters (abonnés de l'application)
                  </Typography>
                }
              />

              {/* Par niveau */}
              <FormControlLabel
                value="niveau"
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: C.textMuted,
                      "&.Mui-checked": { color: C.accentLight },
                      p: "6px",
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "13px", color: C.text }}>
                    Par niveau
                  </Typography>
                }
              />
              {cible === "niveau" && (
                <Box sx={{ ml: 4, mt: 0.5, mb: 1 }}>
                  <Select
                    value={niveau}
                    onChange={(e) => setNiveau(Number(e.target.value))}
                    size="small"
                    sx={{ ...selectSx, minWidth: 120 }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: "var(--c-surface)",
                          border: "1.5px solid #1F1E1B",
                          color: C.text,
                        },
                      },
                    }}
                  >
                    {niveaux.map((n) => (
                      <MenuItem
                        key={n.niveau}
                        value={n.niveau}
                        sx={{ fontSize: "13px", "&:hover": { background: C.accentMuted } }}
                      >
                        Niveau {n.niveau}{n.nom ? ` — ${n.nom}` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {/* Inactifs depuis X jours */}
              <FormControlLabel
                value="inactifs"
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: C.textMuted,
                      "&.Mui-checked": { color: C.accentLight },
                      p: "6px",
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "13px", color: C.text }}>
                    Spotters inactifs depuis X jours (dernier lieu collecté)
                  </Typography>
                }
              />
              {cible === "inactifs" && (
                <Box sx={{ ml: 4, mt: 0.5, mb: 1, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    value={inactiveDays}
                    onChange={(e) => {
                      setInactiveDays(e.target.value);
                      setInactiveFetched(false);
                      setInactiveUserIds([]);
                    }}
                    size="small"
                    type="number"
                    inputProps={{ min: 1 }}
                    placeholder="Ex : 30"
                    sx={{ ...inputSx, width: 100 }}
                  />
                  <Button
                    onClick={fetchInactiveCollectors}
                    disabled={fetchingInactive}
                    size="small"
                    startIcon={
                      fetchingInactive ? (
                        <CircularProgress size={12} sx={{ color: C.accentLight }} />
                      ) : null
                    }
                    sx={{
                      background: C.accentMuted,
                      color: C.accentLight,
                      border: `1px solid ${C.borderAccent}`,
                      fontSize: "11px",
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "6px",
                      px: 1.5,
                      py: "5px",
                      "&:hover": { background: "rgba(31,30,27,0.22)" },
                      "&.Mui-disabled": { color: C.accentLight, opacity: 0.5 },
                    }}
                  >
                    {fetchingInactive ? "Recherche…" : "Rechercher"}
                  </Button>
                  {inactiveFetched && (
                    <Typography sx={{ fontSize: "11px", color: inactiveUserIds.length > 0 ? C.success : C.warning }}>
                      {inactiveUserIds.length > 0
                        ? `${inactiveUserIds.length} Spotter(s) trouvé(s)`
                        : "Aucun Spotter inactif"}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Proches d'une quête — mêmes règles que la liste de l'app et le push de publication */}
              <FormControlLabel
                value="queteProche"
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: C.textMuted,
                      "&.Mui-checked": { color: C.accentLight },
                      p: "6px",
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "13px", color: C.text }}>
                    Spotters proches d'une quête
                  </Typography>
                }
              />
              {cible === "queteProche" && (
                <Box sx={{ ml: 4, mt: 0.5, mb: 1 }}>
                  <Select
                    value={questId}
                    onChange={(e) => setQuestId(String(e.target.value))}
                    displayEmpty
                    size="small"
                    sx={{ ...selectSx, minWidth: 240, maxWidth: "100%" }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: "var(--c-surface)",
                          border: "1.5px solid #1F1E1B",
                          color: C.text,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: "13px" }}>
                      {quetesPubliees.length === 0 ? "Aucune quête publiée" : "Choisir une quête publiée"}
                    </MenuItem>
                    {quetesPubliees.map((q) => (
                      <MenuItem
                        key={q.id}
                        value={q.id}
                        sx={{ fontSize: "13px", "&:hover": { background: C.accentMuted } }}
                      >
                        {q.titre}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography sx={{ fontSize: "11px", color: C.textMuted, mt: 0.75, lineHeight: 1.5 }}>
                    Spotters près de la zone (dernière collecte connue) et remplissant les critères
                    de la quête : niveau, ancienneté, statut, collectes validées. Sans lien saisi,
                    le push ouvre la quête.
                  </Typography>
                </Box>
              )}
            </RadioGroup>
          </Box>

          {/* Envoi immédiat seulement */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              onClick={handleSend}
              disabled={sending}
              startIcon={
                sending ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null
              }
              sx={{
                background: sending
                  ? "rgba(31,30,27,0.5)"
                  : C.accent,
                color: "#fff",
                fontWeight: 700,
                fontSize: "14px",
                textTransform: "none",
                borderRadius: "8px",
                py: 1.4,
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: sending
                    ? "rgba(31,30,27,0.5)"
                    : "#1F1E1B",
                  boxShadow: "none",
                },
                "&.Mui-disabled": { color: "#1F1E1B" },
              }}
            >
              {sending ? "Envoi en cours…" : "Envoyer maintenant"}
            </Button>

          </Box>
        </Box>

        {/* ─────────────────────── PHONE PREVIEW ─────────────────────── */}
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: "100%", lg: "auto" },
            display: "flex",
            justifyContent: { xs: "center", lg: "flex-start" },
          }}
        >
          <PhonePreview titre={titre} corps={corps} />
        </Box>
      </Box>

      {/* ── History table ── */}
      <Box
        sx={{
          mt: 4,
          background: C.card,
          border: "1.5px solid #1F1E1B",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: C.textMuted,
            }}
          />
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: C.text }}>
            Historique d'envoi
          </Typography>
          {history.length > 0 && (
            <Chip
              label={history.length}
              size="small"
              sx={{
                height: 18,
                fontSize: "10px",
                fontWeight: 700,
                background: C.accentMuted,
                color: C.accentLight,
                border: `1px solid ${C.borderAccent}`,
                borderRadius: "5px",
                "& .MuiChip-label": { px: "6px" },
                ml: 0.5,
              }}
            />
          )}
        </Box>

        {/* Empty state */}
        {history.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 6,
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: C.textSub }}>
              {historyError ? "Historique indisponible" : "Aucun push envoyé"}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: C.textMuted }}>
              {historyError ? "Le serveur n'a pas répondu." : "Les 20 derniers envois enregistrés par le serveur apparaîtront ici."}
            </Typography>
          </Box>
        )}

        {/* Table */}
        {history.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Date", "Titre", "Cible", "Nb destinataires", "Statut"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.10em",
                        textTransform: "uppercase",
                        color: C.textMuted,
                        borderBottom: `1px solid ${C.border}`,
                        background: "rgba(31,30,27,0.02)",
                        py: 1.2,
                        px: 2.5,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((entry, i) => (
                  <TableRow
                    key={entry.id}
                    sx={{
                      background:
                        i % 2 === 1 ? "rgba(31,30,27,0.015)" : "transparent",
                      "&:hover": { background: "rgba(31,30,27,0.06)" },
                      "&:last-child td": { borderBottom: "none" },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        color: C.textSub,
                        borderBottom: `1px solid ${C.border}`,
                        py: 1.4,
                        px: 2.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtDate(entry.sent_at)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: C.text,
                        borderBottom: `1px solid ${C.border}`,
                        py: 1.4,
                        px: 2.5,
                        maxWidth: 220,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {entry.titre}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        color: C.textSub,
                        borderBottom: `1px solid ${C.border}`,
                        py: 1.4,
                        px: 2.5,
                      }}
                    >
                      {cibleLabel(entry.cible)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        color: C.textSub,
                        borderBottom: `1px solid ${C.border}`,
                        py: 1.4,
                        px: 2.5,
                        textAlign: "center",
                      }}
                    >
                      {/* -1 : topic Firebase, nombre d'abonnés inconnu */}
                      {entry.destinataires >= 0 ? entry.destinataires : "abonnés"}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: `1px solid ${C.border}`,
                        py: 1.4,
                        px: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          background:
                            entry.statut === "sent"
                              ? "rgba(53,106,15,0.10)"
                              : "rgba(162,58,13,0.10)",
                          border: `1px solid ${
                            entry.statut === "sent"
                              ? "rgba(53,106,15,0.30)"
                              : "rgba(162,58,13,0.30)"
                          }`,
                          borderRadius: "5px",
                          px: "8px",
                          py: "3px",
                        }}
                      >
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background:
                              entry.statut === "sent" ? C.success : C.error,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color:
                              entry.statut === "sent" ? C.success : C.error,
                            lineHeight: 1,
                          }}
                        >
                          {entry.statut === "sent" ? "Envoyé" : entry.statut === "refused" ? "Refusé" : "Erreur"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
